import React from 'react'
import { WImage, Button, EditableTitle, EditableDescription, Tag } from '@components/atoms'
import styles from './EventiCard.module.sass'
import { getIcon } from '@utils/icons'
import { checkUrlAndTrackPageBuilder } from '@utils/index'
import { useRouter } from 'next/router'
import { EventoType } from '@utils/types'

interface Props {
  evento?: EventoType
  imagePos?: 'left' | 'right'
  theme?: 'light' | 'neutrallight' | 'secondarydark' | 'black' | 'primary'
  editMode?: boolean
  className?: string
}

const EventiCard = (props: Props) => {
  const {
    evento = undefined,
    imagePos = 'left',
    theme = 'light',
    editMode = false,
    className = '',
  } = props

  const router = useRouter()

  const componentContent = (
    <>
      <div className={styles.imageBox}>
        {!!evento?.immagine && (
          <WImage
            src={evento.immagine}
            maxWidth={960}
            maxHeight={500}
            objectFit="cover"
            alt={evento.alt}
            title={evento.alt}
          />
        )}
      </div>
      <div>
        <div className={styles.main}>
          <div className={styles.top}>
            {!!evento?.data && (
              <div className={styles.introBox}>
                <Tag
                  label={evento.data}
                  variant={['secondarydark', 'black'].includes(theme) ? 'white' : 'black'}
                  size={'sm'}
                  className={styles.intro}
                  editMode={editMode}
                />
              </div>
            )}

            <EditableTitle title={evento?.titolo} className={styles.title} editMode={editMode} />

            {!!evento?.sottotitolo && (
              <EditableDescription
                description={evento.sottotitolo}
                className={styles.subtitle}
                editMode={editMode}
              />
            )}
          </div>

          {!!evento?.nomeLocation && (
            <div className={styles.locationBox}>
              <div className={styles.locationIcon}>{getIcon(evento.icon)}</div>
              <div className={styles.locationDetails}>
                <EditableDescription
                  description={evento.nomeLocation}
                  className={styles.locationName}
                  editMode={editMode}
                />
                {!!evento?.dettagliLocation && (
                  <EditableDescription
                    description={evento.dettagliLocation}
                    className={styles.locationDetails}
                    editMode={editMode}
                  />
                )}
              </div>
            </div>
          )}

          {!!evento?.descrizione && (
            <EditableDescription
              description={evento.descrizione}
              className={styles.description}
              editMode={editMode}
            />
          )}

          {editMode ? (
            <>{evento?.children}</>
          ) : (
            !!evento?.children?.filter((x) => !!x.url && !!x.ctaLabel)?.length && (
              <div className={styles.ctaBox}>
                {evento.children?.map((x) => (
                  <Button
                    key={x.pk}
                    label={x.ctaLabel}
                    href={x.url}
                    onClick={() =>
                      checkUrlAndTrackPageBuilder(x, router.asPath, evento.titolo || '')
                    }
                    newPage={x.blank}
                    iconLeft={
                      x.iconLeft
                        ? x.iconLeft
                        : !!x.icon && x.iconPos === 'left'
                        ? getIcon(x.icon)
                        : undefined
                    }
                    iconRight={
                      x.iconRight
                        ? x.iconRight
                        : !!x.icon && x.iconPos === 'right'
                        ? getIcon(x.icon)
                        : undefined
                    }
                    variant={x.style as any}
                    color={
                      ['secondary', 'ghost'].includes(x.style) &&
                      ['secondarydark', 'black'].includes(theme)
                        ? 'white'
                        : ''
                    }
                    theme={['secondarydark', 'black'].includes(theme) ? 'dark' : 'light'}
                    size={'lg'}
                    mobileSize={'md'}
                  />
                ))}
              </div>
            )
          )}
        </div>
      </div>
    </>
  )

  return evento ? (
    <div
      className={`${styles.root} ${styles[`root--${theme}`]} ${
        styles[`root--imagePos-${imagePos}`]
      } 
      ${evento.borderTop ? styles[`root--bt`] : ''} 
      ${evento.borderBottom ? styles[`root--bb`] : ''} 
      ${editMode ? styles['root--editMode'] : ''} ${className}`}
    >
      {componentContent}
    </div>
  ) : (
    <></>
  )
}

export default EventiCard
